import { cn } from "@/lib/utils"

export type CreditStatus = "active" | "cancelling" | "paused" | "free"

export interface SidebarCreditChipProps {
  planLabel: string
  status?: CreditStatus
  showUpgrade?: boolean
  onClick?: () => void
  onUpgradeClick?: () => void
  className?: string
}

export function SidebarCreditChip({
  planLabel,
  status = "free",
  showUpgrade,
  onClick,
  onUpgradeClick,
  className,
}: SidebarCreditChipProps) {
  const shouldShowUpgrade = showUpgrade ?? (status === "free" || status === "cancelling")

  let stateBadge: React.ReactNode = null
  if (status === "cancelling") {
    stateBadge = (
      <span className="text-eyebrow text-muted-foreground border border-border rounded px-1.5 py-0.5 shrink-0">
        Ending
      </span>
    )
  } else if (status === "paused") {
    stateBadge = (
      <span className="text-eyebrow text-muted-foreground border border-border rounded px-1.5 py-0.5 shrink-0">
        Paused
      </span>
    )
  }

  const handleUpgrade: React.MouseEventHandler<HTMLSpanElement> = (e) => {
    e.stopPropagation()
    onUpgradeClick?.()
  }

  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full flex flex-wrap items-center gap-x-1.5 gap-y-1.5 text-caption px-3 py-3 hover:bg-muted/60 transition-colors cursor-pointer text-left",
        className,
      )}
    >
      <div className="flex items-center gap-1.5 min-w-0 flex-1">
        <span className="text-label font-medium truncate text-muted-foreground">{planLabel}</span>
        {stateBadge}
      </div>
      {shouldShowUpgrade && (
        <span
          role="button"
          tabIndex={0}
          onClick={handleUpgrade}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault()
              onUpgradeClick?.()
            }
          }}
          className="inline-flex items-center h-6 px-2 rounded-md text-zinc-950 bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 hover:from-amber-300 hover:via-amber-400 hover:to-amber-500 border border-amber-300/60 text-label font-medium shrink-0 cursor-pointer transition-colors"
        >
          Upgrade
        </span>
      )}
    </button>
  )
}
